import type { Color } from "@salestrak/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ErrorLike } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { type BoundsInput, MapClustersDocument, type MapClusterRow } from "#app/lib/data";

import type { MapPin, MapClusterBubble } from "#app/components/map/map-types";
import {
  reconcileRows,
  sameCluster,
  samePin,
} from "#app/components/map/map-utils";
import { getEntityGlyph } from "#app/lib/entity-visuals";

type GqlMapCluster = MapClusterRow;

// Map markers are Google-Maps SVG overlays that can't host an EntityTag, so
// these resolve glyph data through entity-visuals instead of reading entity
// icon fields inline.
export function getStageColor(items: GqlMapCluster["items"]): Color | undefined {
  for (const item of items) {
    const stage = item.entity?.stage;
    if (!stage) continue;
    const color = getEntityGlyph({ kind: "stage", id: stage.stageId, stage }).color;
    if (color) return color;
  }
  return undefined;
}

export function getWorkflowIcon(items: GqlMapCluster["items"]): string | undefined {
  const workflow = items[0]?.entity?.workflow;
  // Icon-less workflows keep plain dot pins — skip the kind-fallback icon.
  if (!workflow?.icon?.icon) return undefined;
  return (
    getEntityGlyph({ kind: "workflow", id: workflow.workflowId, workflow }).icon ??
    undefined
  );
}

export function clusterToPin(m: GqlMapCluster): MapPin {
  return {
    id: m.addressId ?? `pin-${m.latLng.lat},${m.latLng.lng}`,
    latitude: m.latLng.lat,
    longitude: m.latLng.lng,
    color: getStageColor(m.items) ?? "grey",
    count: m.count,
    icon: m.count === 1 ? getWorkflowIcon(m.items) : undefined,
    addressId: m.addressId ?? undefined,
    addressLine1: m.addressLine1 ?? undefined,
    wholeAddress: m.wholeAddress ?? undefined,
    city: m.city ?? undefined,
    stateProvince: m.stateProvince ?? undefined,
    postalCode: m.postalCode ?? undefined,
    issueIds: m.items
      .filter((i) => i.entity?.issueId)
      .map((i) => i.entity!.issueId!),
    propertyDetails: m.propertyDetails ?? undefined,
  };
}

/**
 * The viewport's markers. A query runs when the BOUNDS, the ZOOM rung or the
 * filter change — never on selection alone. `preserveIds` (the selected pin's
 * issues, which the server keeps out of any cluster) is an OPTION the next
 * query carries, not a trigger: selecting a pin that is already on the map
 * used to refetch the whole viewport, and the answer landed mid-flight with a
 * fresh object for every marker. A pin folded into a cluster after a pan is
 * unfolded by the bounds query that pan already fires.
 *
 * Pin and cluster rows keep their IDENTITY across answers (`reconcileRows`):
 * a row the server repeated unchanged is the same object the map already
 * renders, so the memoized markers and vis.gl's `marker.position` binding
 * see a change only where one happened.
 */
/** Map zoom at and past which the server returns only pins, never clusters. */
const NO_CLUSTER_ZOOM = 17;

const getPinId = (pin: MapPin) => pin.id;
const getClusterId = (cluster: MapClusterBubble) => cluster.id;

export function useMapClusters(
  bounds: BoundsInput | null,
  zoom: number,
  where?: Record<string, unknown>,
  preserveIds?: string[],
) {
  const client = useApolloClient();
  const requestIdRef = useRef(0);
  const preserveIdsRef = useRef(preserveIds);
  preserveIdsRef.current = preserveIds;
  const [rawData, setRawData] = useState<GqlMapCluster[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorLike | undefined>();
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);

  // Round zoom so fractional changes that don't cross an integer boundary
  // don't trigger a redundant query
  const roundedZoom = Math.round(zoom);

  useEffect(() => {
    if (!bounds) return;

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);
    const preserveIds = preserveIdsRef.current;

    client
      .query({
        query: MapClustersDocument,
        variables: {
          bounds: {
            north: bounds.north,
            south: bounds.south,
            east: bounds.east,
            west: bounds.west,
          },
          where: where ?? undefined,
          options: {
            zoom: roundedZoom,
            clusterThreshold: 15,
            noClusterZoom: NO_CLUSTER_ZOOM,
            ...(preserveIds?.length ? { preserveIds } : {}),
          },
        },
        fetchPolicy: "no-cache",
      })
      .then(({ data, error }) => {
        if (requestIdRef.current !== requestId) return;
        // errorPolicy "all" resolves with `error` set instead of rejecting.
        if (!data) {
          setError(error);
          setLoading(false);
          return;
        }
        setRawData(data.mapClusters);
        setLoading(false);
      })
      .catch((err) => {
        if (requestIdRef.current !== requestId) return;
        setError(err);
        setLoading(false);
      });
  }, [client, bounds, where, roundedZoom, refreshKey]);

  // Each memo's ref is its own cache of the last answer it returned — the
  // previous rows `reconcileRows` matches the fresh ones against.
  const pinsRef = useRef<MapPin[]>([]);
  const pins = useMemo(() => {
    const next = rawData
      .filter((m: GqlMapCluster) => !m.isCluster)
      .map(clusterToPin);
    const reconciled = reconcileRows(pinsRef.current, next, getPinId, samePin);
    pinsRef.current = reconciled;
    return reconciled;
  }, [rawData]);

  const clustersRef = useRef<MapClusterBubble[]>([]);
  const clusters = useMemo(() => {
    const next = rawData
      .filter((m: GqlMapCluster) => m.isCluster)
      .map(
        (m: GqlMapCluster): MapClusterBubble => ({
          id: `cluster-${m.latLng.lat},${m.latLng.lng}`,
          latitude: m.latLng.lat,
          longitude: m.latLng.lng,
          count: m.count,
          bounds: m.bounds ?? { north: 0, south: 0, east: 0, west: 0 },
        }),
      );
    const reconciled = reconcileRows(
      clustersRef.current,
      next,
      getClusterId,
      sameCluster,
    );
    clustersRef.current = reconciled;
    return reconciled;
  }, [rawData]);

  return { pins, clusters, loading, error, refresh };
}
